import LegalLayout from '@/components/feature/LegalLayout';
import { useTranslation } from 'react-i18next';

export default function AMLPolicy() {
  const { t } = useTranslation();
  return (
    <LegalLayout title={t('aml.title', 'AML Policy')} lastUpdated="May 20, 2026">
      <div className="space-y-8">
        <section>
          <h2 className="text-lg font-semibold mb-3">{t('aml.s1', '1. Policy Statement')}</h2>
          <p className="leading-relaxed">
            ZhiSu is committed to preventing its platform from being used for money laundering, terrorist financing, or any other illicit financial activities. This Anti-Money Laundering (&quot;AML&quot;) and Counter-Terrorist Financing (&quot;CTF&quot;) Policy outlines the measures we implement to detect, prevent, and report suspicious activities in compliance with applicable laws and regulations.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('aml.s2', '2. Regulatory Framework')}</h2>
          <p className="leading-relaxed mb-3">
            Our AML/CTF program is designed to comply with the following international standards and regulations:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>FATF (Financial Action Task Force) Recommendations</li>
            <li>The Anti-Money Laundering and Counter-Terrorist Financing Act (as applicable)</li>
            <li>OFAC (Office of Foreign Assets Control) sanctions lists</li>
            <li>EU Anti-Money Laundering Directives (AMLD5, AMLD6)</li>
            <li>Financial Crimes Enforcement Network (FinCEN) regulations</li>
            <li>Applicable local regulations in jurisdictions where we operate</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('aml.s3', '3. Customer Due Diligence (CDD)')}</h2>
          <p className="leading-relaxed mb-3">
            ZhiSu implements a risk-based approach to customer due diligence:
          </p>
          <h3 className="font-medium mb-2">3.1 Know Your Customer (KYC)</h3>
          <p className="leading-relaxed mb-3">
            Before granting access to certain Services, we may require users to complete identity verification, including:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed mb-4">
            <li>Verification of full legal name, date of birth, and nationality</li>
            <li>Government-issued photo identification (passport, driver's license, or national ID card)</li>
            <li>Proof of address (utility bill, bank statement, or official correspondence)</li>
            <li>Liveness detection and facial recognition matching</li>
            <li>Source of funds documentation for high-value users</li>
          </ul>
          <h3 className="font-medium mb-2">3.2 Risk Classification</h3>
          <p className="leading-relaxed mb-3">
            Users are classified into risk categories based on factors including:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>Low Risk:</strong> Users from low-risk jurisdictions with standard transaction patterns.</li>
            <li><strong>Medium Risk:</strong> Users with higher transaction volumes or from jurisdictions with moderate AML risks.</li>
            <li><strong>High Risk:</strong> Users from high-risk jurisdictions, PEPs (Politically Exposed Persons), or those exhibiting unusual transaction patterns.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('aml.s4', '4. Ongoing Monitoring')}</h2>
          <p className="leading-relaxed mb-3">
            We continuously monitor user activity to identify suspicious transactions:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>Automated transaction monitoring systems analyzing patterns and anomalies</li>
            <li>Screening against sanctions lists, PEP databases, and adverse media</li>
            <li>Real-time alerts for large transactions or rapid movement of funds</li>
            <li>Behavioral analysis to detect structuring, layering, or integration activities</li>
            <li>Periodic re-verification of user identity and risk profiles</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('aml.s5', '5. Prohibited Activities')}</h2>
          <p className="leading-relaxed mb-3">
            The following activities are strictly prohibited on the ZhiSu platform:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>Money laundering in any form, including placement, layering, or integration</li>
            <li>Terrorist financing or providing financial support to sanctioned entities</li>
            <li>Using proceeds from criminal activities to trade on the platform</li>
            <li>Structuring transactions to evade reporting thresholds</li>
            <li>Operating as an unregistered money services business through our platform</li>
            <li>Using mixing services, tumblers, or privacy coins to obscure transaction trails</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('aml.s6', '6. Sanctions Compliance')}</h2>
          <p className="leading-relaxed mb-3">
            ZhiSu strictly prohibits any use of the Services by individuals or entities that are:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>Subject to sanctions administered by OFAC, the UN, EU, UK, or other applicable sanctions authorities</li>
            <li>Residents of comprehensively sanctioned jurisdictions (including Cuba, Iran, North Korea, Syria, and the Crimea, Donetsk, and Luhansk regions)</li>
            <li>Listed on the Specially Designated Nationals and Blocked Persons (SDN) List</li>
            <li>Owned or controlled by sanctioned persons</li>
          </ul>
          <p className="leading-relaxed mt-3">
            We conduct real-time screening of user wallets and transactions against global sanctions lists using industry-leading blockchain analytics tools.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('aml.s7', '7. Suspicious Activity Reporting')}</h2>
          <p className="leading-relaxed mb-3">
            When suspicious activity is detected, ZhiSu will:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>Immediately suspend or restrict the affected account pending investigation</li>
            <li>File Suspicious Activity Reports (SARs) or equivalent reports with relevant authorities</li>
            <li>Preserve all relevant records for a minimum of seven years</li>
            <li>Cooperate fully with law enforcement and regulatory investigations</li>
            <li>Maintain strict confidentiality regarding the filing of reports</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('aml.s8', '8. Record Keeping')}</h2>
          <p className="leading-relaxed">
            We maintain comprehensive records of all AML/CTF activities, including customer identification documents, transaction records, risk assessments, monitoring reports, and staff training records. These records are retained for a minimum of seven years from the date of the transaction or the termination of the business relationship, whichever is later.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('aml.s9', '9. Staff Training')}</h2>
          <p className="leading-relaxed">
            All ZhiSu employees and contractors receive regular training on AML/CTF regulations, red flags for suspicious activity, and internal reporting procedures. Our compliance team stays current with evolving regulatory requirements and industry best practices.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('aml.s10', '10. Independent Audit')}</h2>
          <p className="leading-relaxed">
            Our AML/CTF program is subject to periodic independent audits by qualified third-party firms. Audit findings are reported to senior management and the board of directors, and remedial actions are implemented promptly.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('aml.s11', '11. Contact')}</h2>
          <p className="leading-relaxed">
            For questions regarding our AML Policy or to report suspicious activity, please contact our Compliance Team at compliance@zhisu.trade. All reports are treated with the utmost confidentiality.
          </p>
        </section>
      </div>
    </LegalLayout>
  );
}